import { GeckoFeature } from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import { GeckoFixedSensorJSON } from 'types/assetModel/features/geckoFixedSensor'
import { GeckoNozzleJSON } from 'types/assetModel/features/geckoNozzle'
import { GeckoPlateJSON } from 'types/assetModel/features/geckoPlate'
import { GeckoPipeIntegralSupportJSON } from 'types/assetModel/features/pipeSection/geckoPipeIntegralSupport'
import { GeckoPipeNonIntegralSupportJSON } from 'types/assetModel/features/pipeSection/geckoPipeNonIntegralSupport'
import { GeckoPipeWrapJSON } from 'types/assetModel/features/pipeSection/geckoPipeWrap'
import { GeckoCircumferentialWeldJSON } from 'types/assetModel/features/welds/geckoCircumferentialWeld'
import { GeckoHorizontalWeldJSON } from 'types/assetModel/features/welds/geckoHorizontalWeld'
import { GeckoSeamWeldJSON } from 'types/assetModel/features/welds/geckoSeamWeld'
import { GeckoVerticalWeldJSON } from 'types/assetModel/features/welds/geckoVerticalWeld'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'
import {
  optionalNumberSchema,
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

const { pi } = forecastMath

// Defined but not used
type PipeTJointSupportedFeatures =
  | GeckoFixedSensorJSON
  | GeckoNozzleJSON
  | GeckoPipeNonIntegralSupportJSON
  | GeckoPipeIntegralSupportJSON
  | GeckoCircumferentialWeldJSON
  | GeckoSeamWeldJSON
  | GeckoPipeWrapJSON
  | GeckoPlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export type PipeTJointAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.PipeTJoint
  brace_length: number
  brace_radius: number
  chord_radius: number
  chord_length: number
  material: string | undefined
  installation_date: string | undefined
  features: Record<string, PipeTJointSupportedFeatures>

  clock_bearing_rotation: number | undefined
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const PipeTJointAvailableFeatures: GeckoFeatureModelType[] = []

export const NewPipeTJointAssetModelJSON = {
  name: 'New Pipe T-Joint',
  type: GeckoComponentModelType.PipeTJoint,
  brace_length: 0,
  brace_radius: 0,
  chord_radius: 0,
  chord_length: 0,
  material: undefined,
  installation_date: undefined,
  features: {},

  clock_bearing_rotation: 0,
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as PipeTJointAssetModelJSON

export class GeckoPipeTJoint extends GeckoEditableComponent {
  type: GeckoComponentModelType.PipeTJoint
  brace_length: GeckoMeasurement
  brace_radius: GeckoMeasurement
  chord_length: GeckoMeasurement
  chord_radius: GeckoMeasurement
  material: string | undefined
  installationDate: Date | undefined
  features: Map<string, GeckoFeature>

  clockBearingRotation: number | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    brace_length: GeckoMeasurement,
    brace_radius: GeckoMeasurement,
    chord_radius: GeckoMeasurement,
    chord_length: GeckoMeasurement,
    material: string | undefined,
    installationDate: Date | undefined,

    clockBearingRotation: number | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.PipeTJoint
    this.brace_length = brace_length
    this.brace_radius = brace_radius
    this.chord_length = chord_length
    this.chord_radius = chord_radius
    this.material = material
    this.installationDate = installationDate
    this.clockBearingRotation = clockBearingRotation
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation

    // Features
    this.features = new Map()
  }

  public static CreateFromJSON(id: string, json: PipeTJointAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const braceLength = GeckoMeasurement.CreateFromValue(
      json.brace_length,
      GeckoMeasurementUnit.Meters
    )
    const braceRadius = GeckoMeasurement.CreateFromValue(
      json.brace_radius,
      GeckoMeasurementUnit.Meters
    )
    const chordLength = GeckoMeasurement.CreateFromValue(
      json.chord_length,
      GeckoMeasurementUnit.Meters
    )
    const chordRadius = GeckoMeasurement.CreateFromValue(
      json.chord_radius,
      GeckoMeasurementUnit.Meters
    )
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const clockBearingRotation = parseNumber(json.clock_bearing_rotation)
    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoPipeTJoint(
      id,
      json,
      name,
      braceLength,
      braceRadius,
      chordRadius,
      chordLength,
      material,
      installationDate,
      clockBearingRotation,
      componentBounds,
      sceneConfig,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): PipeTJointAssetModelJSON {
    return generatePipeTJoint(
      this.name,
      this.brace_length,
      this.brace_radius,
      this.chord_length,
      this.chord_radius,
      this.material,
      this.installationDate,
      this.clockBearingRotation,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as PipeTJointAssetModelJSON
    )
  }
}

const generatePipeTJoint = (
  name: string,
  braceLength: GeckoMeasurement,
  braceRadius: GeckoMeasurement,
  chordLength: GeckoMeasurement,
  chordRadius: GeckoMeasurement,
  material: string | undefined,
  installationDate: Date | undefined,
  clockBearingRotation: number | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherPipeTJointData: PipeTJointAssetModelJSON = NewPipeTJointAssetModelJSON
): PipeTJointAssetModelJSON => {
  const bracePipeLength = braceLength
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const bracePipeRadius = braceRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const chordPipeLength = chordLength
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const chordPipeRadius = chordRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // Component bounds are a cylinder encompassing the object
  // Circumference is 2 * pi * r, where r = chord len / 2
  const pipeTJointCircumference = GeckoMeasurement.CreateFromValue(
    chordPipeLength * pi,
    GeckoMeasurementUnit.Meters
  ).toPrecision(assetModelPrecision).num

  // Height is the chord radius * 2 + brace len because the brace rests on the chord
  const pipeTJointHeight = GeckoMeasurement.CreateFromValue(
    2 * chordPipeRadius + bracePipeLength,
    GeckoMeasurementUnit.Meters
  ).num

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoComponentModelType.PipeTJoint,
    brace_length: bracePipeLength,
    brace_radius: bracePipeRadius,
    chord_length: chordPipeLength,
    chord_radius: chordPipeRadius,
    features: {},
    material: material,
    installation_date: parsedInstallationDate,
    clock_bearing_rotation: clockBearingRotation,
    scene_config: otherPipeTJointData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: pipeTJointCircumference,
      y_end: pipeTJointHeight,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const PipeTJointFormFields = (
  pipeTJointJSON: PipeTJointAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Pipe T-Joint Name',
      value: pipeTJointJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'chord_length',
      label: 'Chord Length',
      value: pipeTJointJSON.chord_length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'chord_radius',
      label: 'Chord Radius',
      value: pipeTJointJSON.chord_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'brace_length',
      label: 'Brace Length',
      value: pipeTJointJSON.brace_length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'brace_radius',
      label: 'Brace Radius',
      value: pipeTJointJSON.brace_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: pipeTJointJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(pipeTJointJSON.installation_date, true),
    {
      dataKey: 'clock_bearing_rotation',
      label: 'Clock Rotation',
      value: pipeTJointJSON.clock_bearing_rotation,
      unit_magnitude: null,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    wallThicknessFormField(pipeTJointJSON.nominal.wall_thickness),
    tminFormField(pipeTJointJSON.tmin),
    compassOrientationFormField(pipeTJointJSON.compass_orientation),
  ]
}

export const PipeTJointJSONFromFormFields = (
  component: PipeTJointAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )

  return generatePipeTJoint(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'brace_length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'brace_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'chord_length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'chord_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parseNumber(
      getFormFieldByDataKey(formFields, 'clock_bearing_rotation')?.value
    ),
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
